// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContext } from "@insite/client-framework/Components/ProductContext";
import updateProductInfo from "@insite/client-framework/Store/Components/ProductInfoList/Handlers/UpdateProductInfo";
import { getProductInfoFromList } from "@insite/client-framework/Store/Components/ProductInfoList/ProductInfoListSelectors";
import translate from "@insite/client-framework/Translate";
import { ProductModel } from "@insite/client-framework/Types/ApiModels";
import ProductQuantityOrdered from "@insite/content-library/Components/ProductQuantityOrdered";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import DataTableCell, { DataTableCellProps } from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow from "@insite/mobius/DataTable/DataTableRow";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import Modal from "@insite/mobius/Modal";
import { TextFieldProps } from "@insite/mobius/TextField";
import { HasToasterContext, withToaster } from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CustomerPartNumberField from "@steiner/App/Components/CustomerPartNumbers/CustomerPartNumberField";
import deleteCustomerPartNumber from "@steiner/App/Handlers/CustomerPartNumber/DeleteCustomerPartNumber";
import updateCustomerPartNumberHandler from "@steiner/App/Handlers/CustomerPartNumber/UpdateCutsomerPartNumber";
import { CustomAppState, CustomerProductModel } from "@steiner/App/Store/Reducers";
import ProductAddToCartButton from "@steiner/Overrides/Components/ProductAddToCartButton";
import ProductAddToListLink, { ProductAddToListLinkStyles } from "@steiner/Overrides/Components/ProductAddToListLink";
import { xTranslate } from "@steiner/XDL/Utilities/translate";
import React, { FC, useEffect, useRef, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    widgetId: string;
    product: ProductModel;
    customerProduct: CustomerProductModel;
    showAddToCart?: boolean;
    showAddToList?: boolean;
    labels: { [key: string]: string };
}

const mapStateToProps = (state: CustomAppState, ownProps: OwnProps) => ({
    productInfo: getProductInfoFromList(state, ownProps.widgetId, ownProps.product.id),
});

const mapDispatchToProps = {
    updateProductInfo,
    updateCustomerPartNumberHandler,
    deleteCustomerPartNumber,
};

type Props = OwnProps &
    ResolveThunks<typeof mapDispatchToProps> &
    ReturnType<typeof mapStateToProps> &
    HasToasterContext;

interface CustomerPartNumberItemStyles {
    productDescriptionCell?: DataTableCellProps;
    partNumberCell?: DataTableCellProps;
    editCell?: DataTableCellProps;
    deleteCell?: DataTableCellProps;
    addToCartCell?: DataTableCellProps;
    addToListCell?: DataTableCellProps;
    actionsContainer?: InjectableCss;
    updatePartNumberButton?: ButtonPresentationProps;
    productDescriptionLink?: LinkPresentationProps;
    productDescriptionText?: TypographyPresentationProps;
    modalGridContainer?: GridContainerProps;
    modalGridItem?: GridItemProps;
    modalProductDescriptionLinkStyle?: LinkPresentationProps;
    modalErpNumberLinkStyle?: LinkPresentationProps;
    modalCancelButton?: GridItemProps;
    modalConfirmButton?: GridItemProps;
    editLink?: LinkPresentationProps;
    deleteLink?: LinkPresentationProps;
    productQuantityOrderedStyles?: TextFieldProps;
    productAddToCartButtonStyles?: ButtonPresentationProps;
    productAddToListLinkStyles?: ProductAddToListLinkStyles;
    productDescriptionWrapper?: InjectableCss;
}

export const customerPartNumberItemStyles: CustomerPartNumberItemStyles = {};

const styles = customerPartNumberItemStyles;
const CustomerPartNumberItem: FC<Props> = ({
    product,
    productInfo,
    customerProduct,
    widgetId,
    labels,
    toaster,
    showAddToCart = true,
    showAddToList = true,
    updateProductInfo,
    updateCustomerPartNumberHandler,
    deleteCustomerPartNumber,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [activeProduct, setActiveProduct] = useState({} as CustomerProductModel);
    const [editingState, setEditingState] = useState<Record<string, boolean>>({});
    const [newPartNumber, setNewPartNumber] = useState("");
    const partNumberRef = useRef<HTMLInputElement>(null);
    const editOrUpdateRef = useRef<HTMLDivElement>(null);
    const [customerPartNumber, setCustomerPartNumber] = useState("");
    const updateQuantity = (qtyOrdered: number) => {
        updateProductInfo({ id: widgetId, productId: product.id, qtyOrdered });
    };

    const updateUnitOfMeasure = (unitOfMeasure: string) => {
        updateProductInfo({ id: widgetId, productId: product.id, unitOfMeasure });
    };

    if (!product || !productInfo) {
        return null;
    }

    useEffect(() => {
        if (activeProduct && editingState[`${activeProduct.customerPartNumber}${activeProduct.productErpNumber}`]) {
            partNumberRef.current?.focus();
        }
        // Event listener
        const closeEditMode = (event: MouseEvent) => {
            if (
                partNumberRef.current &&
                editOrUpdateRef.current &&
                !partNumberRef.current.contains(event.target as Node) &&
                !editOrUpdateRef.current.contains(event.target as Node)
            ) {
                if (activeProduct.customerPartNumber && activeProduct.productErpNumber) {
                    setCustomerProductEditingState(
                        activeProduct.customerPartNumber,
                        activeProduct.productErpNumber,
                        false,
                    );
                }
            }
        };
        document.body.addEventListener("mousedown", closeEditMode);
        return () => {
            document.body.removeEventListener("mousedown", closeEditMode);
        };
    }, [activeProduct, editingState]);

    const setCustomerProductEditingState = (partNumber: string, erpNumber: string, state: boolean) => {
        setEditingState(prevState => {
            return {
                ...prevState,
                [`${partNumber}${erpNumber}`]: state,
            };
        });
    };

    const successUpdateHandler = (newPartNumber: string, erpNumber: string) => {
        toaster.addToast({
            body: translate("Customer Part Number Updated Successfully."),
            messageType: "success",
        });
        setCustomerProductEditingState(newPartNumber, erpNumber, false);
    };

    const errorUpdateHandler = (errorMessage: string, customerPartNumber: string, erpNumber: string) => {
        toaster.addToast({
            body: errorMessage,
            messageType: "danger",
        });

        setCustomerProductEditingState(customerPartNumber, erpNumber, false);
    };

    const updateCustomerPartNumber = (customerProduct: CustomerProductModel) => {
        if (customerProduct.customerPartNumber === newPartNumber) {
            toaster.addToast({
                body: translate("Provided part number is the same as the previous part number."),
                messageType: "warning",
            });
            if (customerProduct.productErpNumber) {
                setCustomerProductEditingState(
                    customerProduct.customerPartNumber,
                    customerProduct.productErpNumber,
                    false,
                );
            }
        } else {
            updateCustomerPartNumberHandler({
                customerPartNumber: newPartNumber,
                productNumber: activeProduct.productErpNumber,
                onSuccess: () => {
                    activeProduct.productErpNumber &&
                        successUpdateHandler(newPartNumber, activeProduct.productErpNumber);
                },
                onError: (errorMessage: string) => {
                    if (customerProduct.customerPartNumber && customerProduct.productErpNumber) {
                        errorUpdateHandler(
                            errorMessage,
                            customerProduct.customerPartNumber,
                            customerProduct.productErpNumber,
                        );
                    }
                },
            } as any);
        }
    };

    const deleteCustomerPartNumberHandler = () => {
        deleteCustomerPartNumber({
            customerPartNumber: activeProduct.customerPartNumber,
            productNumber: activeProduct.productErpNumber,
            onSuccess: () =>
                toaster.addToast({
                    body: translate("Customer Part Number Deleted Successfully."),
                    messageType: "success",
                }),
            onError: () =>
                toaster.addToast({
                    body: translate("An error occurred while attempting to delete the Customer Part Number."),
                    messageType: "danger",
                }),
        } as any);

        setIsOpen(false);
    };

    const editClickHandler = (customerProduct: CustomerProductModel) => {
        setActiveProduct(customerProduct);
        setNewPartNumber(customerProduct.customerPartNumber!);
        setEditingState(prevState => {
            const newState: Record<string, boolean> = {};
            Object.keys(prevState).forEach(key => {
                newState[key] = false;
            });
            newState[`${customerProduct.customerPartNumber}${customerProduct.productErpNumber}`] = true;
            return newState;
        });
    };

    const handleUpdateOnKeyDown = (
        event: React.KeyboardEvent<HTMLInputElement | null>,
        customerProduct: CustomerProductModel,
    ) => {
        if (event.key === "Enter") {
            event.preventDefault();
            updateCustomerPartNumber(customerProduct);
        }
    };
    const productContext = {
        product,
        productInfo,
        onUnitOfMeasureChanged: updateUnitOfMeasure,
        onQtyOrderedChanged: updateQuantity,
    };
    const isEditing = editingState[`${customerProduct.customerPartNumber}${customerProduct.productErpNumber}`];

    return (
        <ProductContext.Provider value={productContext}>
            <DataTableRow key={`${customerProduct.customerPartNumber}${customerProduct.productErpNumber}`}>
                <DataTableCell {...{ header: labels.productDescription }} {...styles.productDescriptionCell}>
                    <StyledWrapper {...styles.productDescriptionWrapper}>
                        <Link
                            onClick={() => {
                                setIsOpen(true);
                                setActiveProduct(customerProduct);
                            }}
                            {...styles.deleteLink}
                        ></Link>
                        {customerProduct.productUri ? (
                            <Link {...styles.productDescriptionLink} href={customerProduct.productUri}>
                                {customerProduct.productDescription}
                            </Link>
                        ) : (
                            <Typography {...styles.productDescriptionText}>
                                {customerProduct.productDescription}
                            </Typography>
                        )}
                    </StyledWrapper>
                </DataTableCell>
                <DataTableCell
                    {...styles.partNumberCell}
                    {...{ header: labels.yourPartNumber }}
                    onClick={() => {
                        customerProduct.isVisible && editClickHandler(customerProduct);
                    }}
                >
                    <CustomerPartNumberField
                        isEditing={
                            editingState[`${customerProduct.customerPartNumber}${customerProduct.productErpNumber}`]
                        }
                        customerPartNumber={customerProduct.customerPartNumber}
                        textFieldProps={{
                            placeholder: translate("Enter a new part number"),
                            value: newPartNumber,
                            ref:
                                customerProduct.customerPartNumber === activeProduct.customerPartNumber
                                    ? partNumberRef
                                    : null,
                            required: true,
                            error: !newPartNumber ? translate("Part Number can't be empty") : "",
                            onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
                                setNewPartNumber(e.target.value);
                            },
                            onKeyDown: event => {
                                handleUpdateOnKeyDown(event as any, customerProduct);
                            },
                            "data-test-selector": "customerPartNumber",
                        }}
                    />
                </DataTableCell>
                <DataTableCell {...{ header: labels.edit }} {...styles.editCell}>
                    <StyledWrapper
                        ref={
                            customerProduct.customerPartNumber === activeProduct.customerPartNumber
                                ? editOrUpdateRef
                                : null
                        }
                    >
                        {!isEditing ? (
                            <Link
                                {...styles.editLink}
                                disabled={!customerProduct.isVisible}
                                id="edit"
                                onClick={() => {
                                    editClickHandler(customerProduct);
                                }}
                            >
                                {translate("Edit")}
                            </Link>
                        ) : (
                            <Button
                                disabled={!newPartNumber}
                                onClick={() => {
                                    updateCustomerPartNumber(customerProduct);
                                }}
                                {...styles.updatePartNumberButton}
                            >
                                {translate("Update")}
                            </Button>
                        )}
                    </StyledWrapper>
                </DataTableCell>
                {showAddToCart && (
                    <DataTableCell {...styles.addToCartCell} {...{ header: labels.addToCart }}>
                        <StyledWrapper {...styles.actionsContainer} data-test-selector="customerPartNumber-actions">
                            <ProductQuantityOrdered
                                extendedStyles={styles.productQuantityOrderedStyles}
                                id={`${widgetId}-qty`}
                            />
                            <ProductAddToCartButton
                                displayAsIcon={true}
                                extendedStyles={styles.productAddToCartButtonStyles}
                            />
                        </StyledWrapper>
                    </DataTableCell>
                )}
                {showAddToList && (
                    <DataTableCell {...styles.addToListCell} {...{ header: labels.addToList }}>
                        <ProductAddToListLink
                            data-test-selector="customerPartNumbers-addToListLink"
                            extendedStyles={styles.productAddToListLinkStyles}
                            labelOverride={translate("Product Group")}
                        />
                    </DataTableCell>
                )}
            </DataTableRow>
            <Modal
                sizeVariant="small"
                isOpen={isOpen}
                handleClose={() => {
                    setIsOpen(false);
                }}
                headline={translate("Deletion Confirmation")}
            >
                <GridContainer {...styles.modalGridContainer}>
                    <GridItem {...styles.modalGridItem}>
                        <Typography>
                            {xTranslate(
                                "Are you sure you want to delete the customer part number for product with description: {0} and with ERP number: {1} ",
                                <Link href={activeProduct.productUri} {...styles.modalProductDescriptionLinkStyle}>
                                    {activeProduct.productDescription}
                                </Link>,
                                <Link href={activeProduct.productUri} {...styles.modalErpNumberLinkStyle}>
                                    {activeProduct.productErpNumber}
                                </Link>,
                            )}
                        </Typography>
                    </GridItem>
                    <GridItem {...styles.modalCancelButton}>
                        <Button
                            variant="secondary"
                            onClick={() => {
                                setIsOpen(false);
                            }}
                        >
                            {translate("Cancel")}
                        </Button>
                    </GridItem>
                    <GridItem {...styles.modalConfirmButton}>
                        <Button variant="primary" onClick={deleteCustomerPartNumberHandler}>
                            {translate("Confirm")}
                        </Button>
                    </GridItem>
                </GridContainer>
            </Modal>
        </ProductContext.Provider>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withToaster(CustomerPartNumberItem));
